import axios from 'axios'
import { createHttpClient } from '@/platform/http/client'
import { attachAuthInterceptors } from '@/platform/http/authBridge'
import { platformEnv } from '@/platform/config/env'

const client = createHttpClient(platformEnv.ecomApiUrl)
const binaryClient = axios.create({ baseURL: platformEnv.ecomApiUrl, timeout: 120_000 })
attachAuthInterceptors(binaryClient)
const productsPath = '/v1/admin/catalogs/products'

export interface ProductSummary {
  id: number
  slug: string
  name: string
  summary?: string | null
  status: 'draft' | 'active' | 'archived'
}

export interface ProductImage {
  id: number
  product_id: number
  variant_ids: number[]
  source_type: 'upload' | 'external'
  image_key: string | null
  external_url: string | null
  image_url: string
  alt: string | null
  position: number
  created_at: number
  updated_at: number
}

export interface ProductVideo {
  id: number
  product_id: number
  variant_ids: number[]
  provider: string
  video_url: string
  embed_url: string | null
  storage_key: string | null
  content_type: string | null
  title: string | null
  position: number
  created_at: number
}

export interface ProductVariant {
  id: number
  sku: string | null
  fulfillment_type: 'physical' | 'digital'
  price: number
  discount: number
  status: 'draft' | 'active' | 'archived'
  options: Array<{ option_id: number; option_value_id: number; option_name: string; value: string }>
}

export interface ProductDetails extends ProductSummary {
  summary: string | null
  terms: Array<{ term_id: number; taxonomy_id: number; taxonomy_name: string; parent_id: number | null; code: string; name: string; position: number }>
  options: Array<{ id: number; code: string; name: string; applies_to: 'both' | 'physical' | 'digital'; position: number; values: Array<{ id: number; value: string; position: number }> }>
  variants: ProductVariant[]
  images: ProductImage[]
  videos: ProductVideo[]
}

interface PresignedUpload {
  key: string
  upload_url: string
  required_headers: Record<string, string>
  expires_in: number
  asset_url?: string
}

interface ImageUploadReservation extends PresignedUpload {
  image_id: number
}

async function convertImageToAvif(file: File): Promise<File> {
  if (file.type === 'image/avif') return file
  const form = new FormData()
  form.append('file', file, file.name)
  form.append('format', 'avif')
  try {
    const response = await binaryClient.post<Blob>('/v1/admin/storage/image/convert', form, {
      responseType: 'blob',
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const baseName = file.name.replace(/\.[^.]+$/u, '') || 'image'
    return new File([response.data], `${baseName}.avif`, { type: 'image/avif', lastModified: Date.now() })
  } catch (cause) {
    const responseData = axios.isAxiosError(cause) ? cause.response?.data : null
    const message = responseData instanceof Blob ? await responseData.text() : null
    throw new Error(message || 'Không chuyển đổi được ảnh sang AVIF.')
  }
}

async function uploadFile(kind: 'video', file: File): Promise<PresignedUpload> {
  const { data: upload } = await client.request<PresignedUpload>({
    url: '/v1/admin/storage/presign',
    method: 'POST',
    data: { kind, content_type: file.type, size_bytes: file.size },
  })
  const response = await fetch(upload.upload_url, {
    method: 'PUT',
    headers: upload.required_headers,
    body: file,
  })
  if (!response.ok) throw new Error('Tải video lên thất bại.')
  return upload
}

export const productsApi = {
  async listProducts(page: number, query: string, taxonomyId?: number | null, termId?: number | null, status?: ProductSummary['status'] | null, size = 20) {
    const response = await client.request<ProductSummary[]>({
      url: productsPath,
      params: { page, size, ...(query ? { q: query } : {}), ...(taxonomyId ? { taxonomy_id: taxonomyId } : {}), ...(termId ? { term_id: termId } : {}), ...(status ? { status } : {}) },
    })
    return { products: response.data, pagination: response.pagination }
  },
  async getProduct(id: number) {
    const response = await client.request<ProductDetails>({ url: `${productsPath}/${id}` })
    return response.data
  },
  async createProduct(input: { name: string; slug: string; summary: string | null; status: ProductSummary['status'] }) {
    const response = await client.request<ProductSummary>({ url: productsPath, method: 'POST', data: input })
    return response.data
  },
  async updateProduct(id: number, input: { name: string; slug: string; summary: string | null; status: ProductSummary['status'] }) {
    const response = await client.request<ProductSummary>({ url: `${productsPath}/${id}`, method: 'PATCH', data: input })
    return response.data
  },
  async updateProductTerms(id: number, termIds: number[]) {
    await client.request({ url: `${productsPath}/${id}/terms`, method: 'PUT', data: { term_ids: termIds } })
  },
  async createOption(productId: number, input: { code: string; name: string; applies_to: 'both' | 'physical' | 'digital'; position: number }) {
    const response = await client.request<{ id: number }>({ url: `${productsPath}/${productId}/options`, method: 'POST', data: input })
    return response.data
  },
  async updateOption(productId: number, optionId: number, input: { name?: string; code?: string; applies_to?: 'both' | 'physical' | 'digital'; position?: number }) {
    const response = await client.request({ url: `${productsPath}/${productId}/options/${optionId}`, method: 'PATCH', data: input })
    return response.data
  },
  async deleteOption(productId: number, optionId: number) {
    await client.request<void>({ url: `${productsPath}/${productId}/options/${optionId}`, method: 'DELETE' })
  },
  async createOptionValue(productId: number, optionId: number, input: { value: string; position: number }) {
    const response = await client.request<{ id: number }>({ url: `${productsPath}/${productId}/options/${optionId}/values`, method: 'POST', data: input })
    return response.data
  },
  async updateOptionValue(productId: number, optionId: number, valueId: number, input: { value?: string; position?: number }) {
    const response = await client.request({ url: `${productsPath}/${productId}/options/${optionId}/values/${valueId}`, method: 'PATCH', data: input })
    return response.data
  },
  async deleteOptionValue(productId: number, optionId: number, valueId: number) {
    await client.request<void>({ url: `${productsPath}/${productId}/options/${optionId}/values/${valueId}`, method: 'DELETE' })
  },
  async saveVariantBatch(productId: number, input: {
    variants: Array<{
      sku: string | null
      fulfillment_type: ProductVariant['fulfillment_type']
      price: number
      discount: number
      status: ProductVariant['status']
      options: Array<{ option_id: number; option_value_id: number }>
    }>
    archive_variant_ids: number[]
  }) {
    await client.request<{ created_count: number; updated_count: number; archived_count: number }>({
      url: `${productsPath}/${productId}/variants/batch`, method: 'PUT', data: input,
    })
  },
  async updateVariant(productId: number, variantId: number, input: Partial<Pick<ProductVariant, 'sku' | 'fulfillment_type' | 'price' | 'discount' | 'status'>> & { options?: Array<{ option_id: number; option_value_id: number }> }) {
    const response = await client.request<ProductVariant>({ url: `${productsPath}/${productId}/variants/${variantId}`, method: 'PATCH', data: input })
    return response.data
  },
  async updateVariantOptions(productId: number, variantId: number, options: Array<{ option_id: number; option_value_id: number }>) {
    const response = await client.request<ProductVariant>({ url: `${productsPath}/${productId}/variants/${variantId}/options`, method: 'PUT', data: { options } })
    return response.data
  },
  async deleteVariant(productId: number, variantId: number) {
    await client.request<void>({ url: `${productsPath}/${productId}/variants/${variantId}`, method: 'DELETE' })
  },
  async uploadImage(productId: number, file: File, position: number, variantIds: number[] = []) {
    const converted = await convertImageToAvif(file)
    const { data: upload } = await client.request<ImageUploadReservation>({
      url: `${productsPath}/${productId}/images/presign`,
      method: 'POST',
      data: { position, alt: null, variant_ids: variantIds },
    })
    try {
      const response = await fetch(upload.upload_url, {
        method: 'PUT',
        headers: upload.required_headers,
        body: converted,
      })
      if (!response.ok) throw new Error(`Bucket từ chối upload ảnh (${response.status}).`)
      const complete = await client.request<ProductImage>({
        url: `${productsPath}/${productId}/images/${upload.image_id}/complete`,
        method: 'POST',
      })
      return complete.data
    } catch (cause) {
      await client.request<void>({ url: `${productsPath}/${productId}/images/${upload.image_id}`, method: 'DELETE' }).catch(() => undefined)
      if (cause instanceof TypeError) throw new Error('Không kết nối được bucket để tải ảnh lên. Kiểm tra CORS của bucket ecom-bucket.')
      throw cause
    }
  },
  async createExternalImage(productId: number, url: string, position: number) {
    const response = await client.request<ProductImage>({
      url: `${productsPath}/${productId}/images`,
      method: 'POST',
      data: { url, position, alt: null, variant_ids: [] },
    })
    return response.data
  },
  async updateImage(productId: number, imageId: number, input: { alt?: string | null; position?: number; variant_ids?: number[] }) {
    const response = await client.request<ProductImage>({
      url: `${productsPath}/${productId}/images/${imageId}`, method: 'PATCH', data: input,
    })
    return response.data
  },
  async replaceImage(productId: number, imageId: number, file: File) {
    const converted = await convertImageToAvif(file)
    const { data: upload } = await client.request<PresignedUpload>({
      url: `${productsPath}/${productId}/images/${imageId}/presign`,
      method: 'POST',
    })
    try {
      const response = await fetch(upload.upload_url, {
        method: 'PUT',
        headers: upload.required_headers,
        body: converted,
      })
      if (!response.ok) throw new Error(`Bucket từ chối thay ảnh (${response.status}).`)
      const complete = await client.request<ProductImage>({
        url: `${productsPath}/${productId}/images/${imageId}/complete`,
        method: 'POST',
      })
      return complete.data
    } catch (cause) {
      if (cause instanceof TypeError) throw new Error('Không kết nối được bucket để thay ảnh. Kiểm tra CORS của bucket ecom-bucket.')
      throw cause
    }
  },
  async deleteImage(productId: number, imageId: number) {
    await client.request<void>({ url: `${productsPath}/${productId}/images/${imageId}`, method: 'DELETE' })
  },
  async orderImages(productId: number, orderedIds: number[]) {
    await client.request({ url: `${productsPath}/${productId}/images/order`, method: 'PUT', data: { ordered_ids: orderedIds } })
  },
  async uploadVideo(file: File) {
    const upload = await uploadFile('video', file)
    return { storage_key: upload.key, content_type: file.type }
  },
  async createVideo(productId: number, input: { video_url?: string; embed_url?: string; provider?: string; storage_key?: string; content_type?: string; title: string | null; position: number; variant_ids?: number[] }) {
    const response = await client.request<ProductVideo>({
      url: `${productsPath}/${productId}/videos`, method: 'POST', data: input,
    })
    return response.data
  },
  async updateVideo(productId: number, videoId: number, input: { title?: string | null; position?: number; variant_ids?: number[] }) {
    const response = await client.request<ProductVideo>({
      url: `${productsPath}/${productId}/videos/${videoId}`, method: 'PATCH', data: input,
    })
    return response.data
  },
  async deleteVideo(productId: number, videoId: number) {
    await client.request<void>({ url: `${productsPath}/${productId}/videos/${videoId}`, method: 'DELETE' })
  },
  async orderVideos(productId: number, orderedIds: number[]) {
    await client.request({ url: `${productsPath}/${productId}/videos/order`, method: 'PUT', data: { ordered_ids: orderedIds } })
  },
}
